import { useCallback, useEffect, useState } from "react";
import NotificationDropdown from "./NotificationDropdown";
import NotificationDrawer from "./NotificationDrawer";
import NotificationFeed from "./NotificationFeed";
import Pusher from "pusher-js";
import { useNavigate } from "react-router";
import { useUserStore } from "(store)/useUserStore";
import { audio1 } from "assets/audio";
import { NotificationToast } from "components";
import { notificationStore } from "(store)/notificationStore";
import {
  getAdminNotifications,
  markReadAdminNotification,
} from "services/notification.service";
import { debounce, getUnreadNotifications } from "utils";
import { toast } from "react-toastify";

function NotificationWidget() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isRenderComponent, setIsRenderComponent] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({ search: "", isRead: "" });
  const [loading, setLoading] = useState(false);

  const userInfo = useUserStore((state) => state.userInfo);
  const { notifications, setNotifications, unreadCount, setUnreadCount } =
    notificationStore();
  const navigate = useNavigate();

  const handleRedirect = useCallback(
    (redirect, isRead, id) => {
      if (isRead === false) {
        setUnreadCount(unreadCount - 1 < 0 ? 0 : unreadCount - 1);

        markReadAdminNotification({ notificationIds: [id] })
          .then(() => {
            setIsRenderComponent((prev) => !prev);
          })
          .catch(() => {
            setUnreadCount(unreadCount);
          });
      }
      navigate(redirect);
    },
    [unreadCount]
  );

  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilter((prev) => ({ ...prev, search: value }));
    }, 500),
    []
  );

  // Handle Search change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER,
    });
    const channel = pusher.subscribe(`notifications-${userInfo?._id}`);
    channel.bind("push-notification", function (data) {
      const newData = [data, ...notifications];
      if (userInfo.isNotificationSound) {
        const notificationSound = new Audio(audio1);
        notificationSound.play().catch((error) => {
          console.error("Error playing notification sound:", error);
        });
      }
      if (userInfo.isPushNotification) {
        NotificationToast(data);
      }
      setNotifications(newData);
      setUnreadCount(unreadCount + 1);
    });

    return () => {
      channel.unbind("push-notification");
      pusher.unsubscribe();
    };
  }, [userInfo, notifications, unreadCount]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        await getAdminNotifications(filter).then((res) => {
          setNotifications(res.data);
          if (!isFirstRender) return;
          setIsFirstRender(false);
          setUnreadCount(getUnreadNotifications(res.data));
        });
      } catch (error) {
        toast.error("Unable to get notifications");
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [isRenderComponent, filter]);

  return (
    <div>
      <NotificationDropdown
        onExpand={() => setIsDrawerOpen(true)}
        notifications={notifications.slice(0, 4)}
        handleRedirect={handleRedirect}
        setUnreadCount={setUnreadCount}
      />
      <NotificationDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setFilter({ search: "", isRead: "" });
        }}
      >
        <NotificationFeed
          notifications={notifications}
          handleRedirect={(redirect, isRead, id) => {
            handleRedirect(redirect, isRead, id);
            setIsDrawerOpen(false);
          }}
          search={search}
          setSearch={handleSearchChange}
          loading={loading}
          setFilter={setFilter}
          filter={filter}
          setIsRenderComponent={setIsRenderComponent}
        />
      </NotificationDrawer>
    </div>
  );
}
export default NotificationWidget;
